import Service from './Service';
import {
  QuestionReport,
  ReportCategory,
  ReportDetail,
  ReportGroup,
  ReportStatus,
} from '../@types/Forum';

export interface CreateReportBody {
  category: ReportCategory;
  /** 1..1000 caracteres, obrigatória */
  description: string;
  /** link de referência opcional */
  reference?: string;
}

export interface ReportListParams {
  status: ReportStatus;
  category?: ReportCategory;
  /** busca por nº da questão, matéria ou trecho da denúncia */
  q?: string;
}

export interface AcceptReportBody {
  /** notificar os denunciantes (in-system; sem e-mail/push — R15) */
  notify: boolean;
  /** encaminhar a questão para a equipe de conteúdo revisar */
  sendToContentTeam: boolean;
}

/**
 * Corpo de `resolve`. Espelha `AcceptReportBody` **menos**
 * `sendToContentTeam`: encerrar como corrigida significa que a correção já foi
 * feita, então não há o que encaminhar. Os dois campos são opcionais no Go
 * (`bindOptionalJSON`), e `notify` ausente vale `true`.
 */
export interface ResolveReportBody {
  /** notificar os denunciantes; ausente = `true` no servidor */
  notify?: boolean;
  /** mensagem do moderador, que vira o corpo do aviso (máx. 2000 caracteres) */
  message?: string;
}

/**
 * Denúncia de questão pelo aluno + triagem administrativa (fila por questão,
 * resposta aos denunciantes, rejeição, desativação e desfazer).
 *
 * Separado de `ForumService` de propósito (decisão 1 do design): comentários e
 * denúncias são fases e domínios distintos.
 *
 * Todas as rotas usadas aqui existem no Go API (`back/cmd/api/routes.go:396-409`,
 * handlers em `back/cmd/api/question_reports.go`).
 */
class QuestionReportService extends Service {
  /**
   * Tradução de vocabulário: a categoria canônica do front para "gabarito
   * incorreto" é `wrong_answer`, mas o Go grava e valida **`answer_key`**.
   * Divergências do back (ou grafias em pt-BR vindas de dados antigos) são
   * absorvidas aqui, no serviço — nunca nas telas.
   */
  static toCategory(value: unknown): ReportCategory {
    const folded = String(value ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
    if (['wrong_answer', 'answer_key', 'gabarito incorreto', 'gabarito errado'].includes(folded))
      return 'wrong_answer';
    if (['outdated_law', 'lei desatualizada', 'desatualizada'].includes(folded))
      return 'outdated_law';
    if (['statement_error', 'erro no enunciado', 'enunciado'].includes(folded))
      return 'statement_error';
    if (['annulled', 'anulada'].includes(folded)) return 'annulled';
    return 'other';
  }

  private static toBackendCategory(category: ReportCategory): string {
    return category === 'wrong_answer' ? 'answer_key' : category;
  }

  /**
   * `GET /moderation/reports` devolve `question` (o `QuestionRef` do Go:
   * `shortRef`/`agency`/`year`/`subject`, sem id nem número), e o detalhe vem
   * com tudo aninhado em `question` + `choices {label, description, correct}`.
   * Estes normalizadores achatam para o vocabulário canônico de
   * `@types/Forum.ts` (`questionRef.number`, `alternatives {choice, text}`,
   * `correctChoice`), que é o que as telas leem.
   */
  private static normalizeRef(raw: any, questionId: string) {
    return {
      id: String(raw?.id ?? questionId),
      number: String(raw?.number ?? raw?.shortRef ?? questionId).replace(/^#/, ''),
      agency: raw?.agency,
      year: raw?.year,
      subject: raw?.subject,
      subjectId: raw?.subjectId,
    };
  }

  private static normalizeGroup(raw: any): ReportGroup {
    return {
      questionId: String(raw?.questionId ?? ''),
      questionRef: this.normalizeRef(raw?.questionRef ?? raw?.question, String(raw?.questionId ?? '')),
      category: this.toCategory(raw?.category),
      count: Number(raw?.count ?? 0),
      lastAt: raw?.lastAt,
      status: raw?.status,
    };
  }

  private static normalizeDetail(raw: any, questionId: string): ReportDetail {
    if (raw?.alternatives) return raw as ReportDetail; // já no formato canônico
    const q = raw?.question ?? {};
    const choices: any[] = Array.isArray(q.choices) ? q.choices : [];
    const letters = ['A', 'B', 'C', 'D', 'E'];
    const reports: any[] = Array.isArray(raw?.reports) ? raw.reports : [];
    return {
      questionId: String(q.id ?? questionId),
      questionRef: this.normalizeRef(q, String(q.id ?? questionId)),
      status: reports[0]?.status ?? 'open',
      statement: q.statement ?? '',
      alternatives: choices.map((c, i) => ({
        choice: c.label || letters[i],
        text: c.description ?? '',
      })),
      correctChoice:
        choices.find((c) => c.correct)?.label ||
        letters[choices.findIndex((c) => c.correct)] ||
        '',
      disabled: Boolean(q.disabled),
      // O Go manda `choice` com o TEXTO da alternativa e a letra em `label`;
      // o canônico é `choice` = letra ("A", "B"…).
      answerStats: {
        total: Number(raw?.answerStats?.total ?? 0),
        byChoice: (raw?.answerStats?.byChoice ?? []).map((s: any, i: number) => ({
          choice:
            typeof s.label === 'string' && s.label
              ? s.label
              : String(s.choice ?? '').length <= 2
                ? String(s.choice ?? '')
                : letters[i] ?? String(s.choice ?? ''),
          pct: Number(s.pct ?? 0),
          count: s.count,
        })),
      },
      reports: reports.map((r) => ({
        id: String(r.id ?? ''),
        questionId: String(q.id ?? questionId),
        category: this.toCategory(r.category),
        description: r.description ?? '',
        reference: r.reference,
        status: r.status ?? 'open',
        author: {
          id: String(r.author?.id ?? ''),
          name: r.author?.name ?? '—',
          role: r.author?.role === 'moderador' || r.author?.role === 'professor' ? r.author.role : 'aluno',
          label: r.author?.label,
        },
        createdAt: r.createdAt,
      })),
    };
  }

  /* ---------------------------------------------------------------- aluno */

  /** `201`; `409` se o usuário já tem denúncia aberta nesta questão. */
  static createReport(questionId: string, body: CreateReportBody) {
    return this.Http.post<QuestionReport>(`/questions/${questionId}/reports`, {
      ...body,
      category: this.toBackendCategory(body.category),
    }).then(this.getData);
  }

  /* -------------------------------------------------------------- triagem */

  /**
   * Fila agrupada por questão, uma chamada por sub-fila — Abertas (`open`),
   * Corrigidas (`resolved`), Desativadas (`accepted`), Rejeitadas (`rejected`).
   */
  static listReports(params: ReportListParams) {
    return this.Http.get<any[]>('/moderation/reports', { params })
      .then(this.getData)
      .then((groups) => (Array.isArray(groups) ? groups : []).map((g) => this.normalizeGroup(g)));
  }

  /** Questão + gabarito + distribuição de respostas + denúncias recebidas. */
  static getByQuestion(questionId: string) {
    return this.Http.get<any>(`/moderation/reports/by-question/${questionId}`)
      .then(this.getData)
      .then((raw) => this.normalizeDetail(raw, questionId));
  }

  /** Resposta única aos denunciantes — "Notificar os N alunos". */
  static respond(questionId: string, message: string) {
    return this.Http.post(`/moderation/reports/by-question/${questionId}/respond`, {
      message,
    }).then(this.getData);
  }

  /** Fecha a denúncia como rejeitada; a questão permanece ativa. */
  static reject(questionId: string) {
    return this.Http.post(`/moderation/reports/by-question/${questionId}/reject`).then(
      this.getData
    );
  }

  /**
   * Encerra as denúncias abertas da questão como **procedente e corrigida**:
   * grava `resolved` sem tocar em `disabled`. Terminal — o back não tem
   * `undo-resolve`, porque não houve efeito na questão para reverter.
   *
   * `404` quando a questão não tem nenhuma denúncia `open`: é o que o moderador
   * encontra quando outro decidiu primeiro, e a tela trata como aviso, não como
   * erro.
   */
  static resolve(questionId: string, body?: ResolveReportBody) {
    return this.Http.post<{ disabled: false; notified: number }>(
      `/moderation/reports/by-question/${questionId}/resolve`,
      body
    ).then(this.getData);
  }

  /** Marca procedente e **desativa a questão** (reusa a mecânica de `disabled`). */
  static accept(questionId: string, body: AcceptReportBody) {
    return this.Http.post(
      `/moderation/reports/by-question/${questionId}/accept`,
      body
    ).then(this.getData);
  }

  /**
   * Reativa a questão e reabre a denúncia — o "Desfazer" do toast.
   * É um endpoint próprio, não um atraso no cliente (decisão 9 do design): o
   * efeito precisa valer para todos e sobreviver a fechar a aba.
   */
  static undoAccept(questionId: string) {
    return this.Http.post(`/moderation/reports/by-question/${questionId}/undo-accept`).then(
      this.getData
    );
  }
}

export default QuestionReportService;
